import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  FaVideo,
  FaPoundSign,
  FaExchangeAlt,
  FaCar,
  FaThumbsUp,
  FaList,
  FaCarSide,
  FaCalendarAlt,
  FaSearch,
  FaTicketAlt,
} from "react-icons/fa";
import { MdSell } from "react-icons/md";

import TestDriveForm from "./TestDriveForm";
import PartExForm from "./PartExForm";
import ContactUsForm from "./ContactUsForm";
import Thinking from "./Thinking";

/**
 * Chat-only MotoMate widget that can be embedded on a dealer website.
 *
 * Accepted props / config (all optional but recommended):
 *  - vehicleReg: string (e.g. "AB12 CDE")
 *  - reserved: boolean (e.g. true if the vehicle is reserved)
 *  - description: string (short marketing blurb for the vehicle)
 *
 * Fallbacks if props are not provided:
 *  - Will look for global window.MotoMate?.config
 *  - Will also read URL params: ?vehicleReg=...&reserved=true&description=...
 *
 * Server endpoint expected:
 *  POST /api/chat  { messages, vehicleContext }
 *  -> returns plain text assistant reply
 */

// Utility to read a config value from props, then window, then URL params
function resolveConfig(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const globalCfg = (window.MotoMate && window.MotoMate.config) || {};

  const get = (key, fallback = undefined) => {
    if (props[key] !== undefined && props[key] !== null) return props[key];
    if (globalCfg[key] !== undefined && globalCfg[key] !== null)
      return globalCfg[key];
    if (urlParams.has(key)) return urlParams.get(key);
    return fallback;
  };

  // Coerce reserved into a boolean when coming from URL/global
  const reservedRaw = get("reserved", false);
  const reserved =
    typeof reservedRaw === "string"
      ? ["1", "true", "yes"].includes(reservedRaw.toLowerCase())
      : Boolean(reservedRaw);

  return {
    vehicleReg: get("vehicleReg", "UNKNOWN"),
    reserved,
    description: get(
      "description",
      "No description provided. Ask me about history, spec, running costs and finance."
    ),
  };
}

function pollStripePayment(c, onSend) {
  if (c.href && c.href.includes("stripe.com")) {
    // Start polling for stripe payment status immediately
    let pollCount = 0;
    const maxPolls = 60; // 5 minutes with 5-second intervals
    const pollInterval = setInterval(async () => {
      try {
        const clientSessionKey = "motomate_client_session_id";
        let clientId;
        try {
          clientId = window.localStorage.getItem(clientSessionKey);
        } catch (e) {
          clientId = "sess-" + Math.random().toString(36).slice(2, 10);
        }

        const response = await fetch(
          `/api/stripe-status?clientId=${encodeURIComponent(clientId)}`
        );

        if (response.ok) {
          const data = await response.json();

          if (data === true || data.stripePaymentComplete === true) {
            clearInterval(pollInterval);
            onSend(
              "I have completed my £100 stripe payment for my vehicle reservation."
            );
            return;
          }
        }
      } catch (error) {
        console.error("Error checking Stripe status:", error);
      }

      pollCount++;
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
      }
    }, 5000);
  }
}

function normalizeToCard(content) {
  if (typeof content === "string") {
    return { type: "text", message: content, ctas: [] };
  }

  // NEW: pass-through form cards
  if (content && content.type === "form") {
    return content; // { type: "form", formId: "testDrive" }
  }

  if (
    content &&
    content.type === "text" &&
    typeof content.message === "string"
  ) {
    return { type: "text", message: content.message, ctas: content.ctas || [] };
  }

  return null;
}

function AutoOpenForm({ c, setOpenForm }) {
  const openedRef = React.useRef(false);

  React.useEffect(() => {
    if (!openedRef.current) {
      setOpenForm({ formId: c.formId || "testDrive" });
      openedRef.current = true; // guard against double-invoke in Strict Mode
    }
  }, [c.formId, setOpenForm]);

  return null; // <-- no button rendered
}

function renderMessage(
  m,
  {
    onSend,
    setVehicleReg,
    setReserved,
    setDescription,
    vehicleReg,
    setOpenForm,
    handleFormSubmitFromCard,
  } = {}
) {
  const card = normalizeToCard(m.content);

  // NEW: server-driven form card inside the chat stream
  if (card && card.type === "form") {
    if (card.formId === "partEx" || card.formId === "sellMyCar") {
      return (
        <PartExForm
          vehicleReg={vehicleReg}
          onCancel={() => setOpenForm(null)}
          onSubmitted={(payload) => handleFormSubmitFromCard(payload)}
          formId={card.formId}
        />
      );
    }
    // Unknown form: show JSON so you can debug
    return <pre>{JSON.stringify(card, null, 2)}</pre>;
  }

  // NEW: server-driven form card inside the chat stream
  if (card && card.type === "form") {
    if (card.formId === "contactUs") {
      return (
        <ContactUsForm
          vehicleReg={vehicleReg}
          onCancel={() => setOpenForm(null)}
          onSubmitted={(payload) => handleFormSubmitFromCard(payload)}
        />
      );
    }
    // Unknown form: show JSON so you can debug
    return <pre>{JSON.stringify(card, null, 2)}</pre>;
  }

  // NEW: server-driven form card inside the chat stream
  if (card && card.type === "form") {
    if (
      card.formId === "testDrive" ||
      card.formId === "videoWalkaround" ||
      card.formId === "oneToOneSalesCall" ||
      card.formId === "carViewingAppointment"
    ) {
      return (
        <TestDriveForm
          vehicleReg={vehicleReg}
          onCancel={() => setOpenForm(null)}
          onSubmitted={(payload) => handleFormSubmitFromCard(payload)}
          formId={card.formId}
        />
      );
    }
    // Unknown form: show JSON so you can debug
    return <pre>{JSON.stringify(card, null, 2)}</pre>;
  }

  const iconMap = {
    FaVideo,
    FaPoundSign,
    FaExchangeAlt,
    FaCar,
    FaThumbsUp,
    FaList,
    FaCarSide,
    FaCalendarAlt,
    FaSearch,
    MdSell,
    FaTicketAlt,
  };

  if (card && card.type === "text") {
    const ctas = Array.isArray(card.ctas) ? card.ctas : [];
    // console.log(ctas);
    return (
      <div>
        <ReactMarkdown
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
        >
          {card.message}
        </ReactMarkdown>

        {ctas.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column", // ⟵ stack vertically
              gap: 8,
              marginTop: 8,
            }}
          >
            {ctas.map((c, i) =>
              c.type === "forceForm" ? (
                <AutoOpenForm key={i} c={c} setOpenForm={setOpenForm} />
              ) : c.type === "showForm" ? (
                <button
                  key={i}
                  onClick={() =>
                    setOpenForm({ formId: c.formId || "testDrive" })
                  }
                  style={{ ...btnStyle, display: "block" }}
                >
                  {c.label || "Open form"}
                </button>
              ) : c.type === "link" ? (
                <a
                  key={i}
                  href={c.href}
                  target={c.target || "_self"}
                  rel="noopener noreferrer"
                  style={{ ...btnStyle, display: "block" }}
                  onClick={() => pollStripePayment(c, onSend)}
                >
                  {c.label}
                </a>
              ) : c.type === "icon" ? (
                <button
                  key={i}
                  onClick={() => {
                    if (c.type === "icon") {
                      const textToSend =
                        c.postbackText || c.text || c.label || "OK";
                      onSend?.(textToSend, {
                        payload: c.payload,
                        source: "cta",
                      });
                    }
                  }}
                  style={iconBtnStyle}
                  title={c.tooltip || c.label}
                  aria-label={c.ariaLabel || c.label}
                >
                  {iconMap[c.label]
                    ? React.createElement(iconMap[c.label], { size: 18 })
                    : "❓"}
                </button>
              ) : (
                <button
                  key={i}
                  onClick={() => {
                    const newReg = c.switchRegistration?.trim();
                    if (newReg) setVehicleReg(newReg); // update UI
                    if (newReg) setReserved(false); // update UI
                    if (newReg) setDescription(""); // update UI

                    if (c.type === "postback") {
                      const textToSend =
                        c.postbackText || c.text || c.label || "OK";
                      onSend?.(textToSend, {
                        payload: c.payload,
                        source: "cta",
                        overrideVehicleReg: newReg, // <-- ensure server call uses new reg now
                      });
                    }
                  }}
                  style={{ ...btnStyle, display: "block" }}
                >
                  {c.label}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  }

  return <pre>{JSON.stringify(m.content, null, 2)}</pre>;
}

const btnStyle = {
  textAlign: "center",
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: "#E4B343",
  color: "#000",
  cursor: "pointer",
  textDecoration: "none",
};

const btnStyleLink = {
  textAlign: "center",
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: "#E4B343",
  color: "#fff",
  cursor: "pointer",
  textDecoration: "none",
};

const iconBtnStyle = {
  all: "unset", // reset browser defaults
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: "50%", // circular
  backgroundColor: "#E4B343", // gold background (adjust to theme)
  color: "#fff", // white icon
  boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  margin: "4px",
  transition: "background-color 0.2s, transform 0.1s",
  marginLeft: "auto", // pushes it to the right if parent is flex
};

function formatDateToUK(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

export default function App(props) {
  const [vehicleReg, setVehicleReg] = useState(resolveConfig(props).vehicleReg);
  const [reserved, setReserved] = useState(resolveConfig(props).reserved);
  const [description, setDescription] = useState(
    resolveConfig(props).description
  );

  const initialVehicleReg = useRef(vehicleReg);
  const [responseCount, setResponseCount] = useState(0);
  const hasShownVehicleRegMessage = useRef(false);
  const prevVehicleReg = useRef(vehicleReg);

  // Variable to control after how many responses to show the form
  const RESPONSES_BEFORE_FORM_1ST = 4;
  const RESPONSES_BEFORE_FORM_2ND = 99;

  // if vehicle reg is populated on first load, call my server api to get make and model.
  // Do NOT re-run if vehicleReg changes later.
  useEffect(() => {
    // Use the initialVehicleReg (captured on first render) so this effect can run only once on mount
    const vrm = initialVehicleReg.current;

    let welcomeMsg = `Hi, I'm MotoMate, your smart car assistant.`;
    if (vrm && vrm !== "UNKNOWN") {
      welcomeMsg = `Hi, I'm MotoMate, your smart car assistant. This chat is about the vehicle  ${vrm}.`;
    }

    const genericMsg =
      "I can help with specs, finance, part-ex, MOT, videos and more. Alternatively, click the buttons below for; a video demonstration, finance options, part exchange or to book a test drive. Before we begin, can I kindly ask for your contact details?";

    if (!vrm || vrm === "UNKNOWN") {
      // No registration provided on first load — show generic help message
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `${welcomeMsg} I can help you find the right vehicle or give you information about our dealership such as openening hours or finance options. Before we begin, can I kindly ask for your contact details?`,
        },
      ]);
      // Show the contact us form right away
      setOpenForm({ formId: "contactUs" });
      return;
    }

    let mounted = true;

    fetch(`/api/stock`)
      .then((res) => {
        if (!res.ok) return [];
        return res.json().catch(() => []);
      })
      .then((stock) => {
        if (!mounted) return;

        const normalize = (s) =>
          (s || "").toString().toUpperCase().replace(/\s+/g, "");
        const target = normalize(vrm);

        const match =
          Array.isArray(stock) &&
          stock.find((item) => normalize(item.registration) === target);

        if (match && match.make && match.model) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: `${welcomeMsg} ${match.make} ${match.model}. ${genericMsg}`,
            },
          ]);
          // Count this as a response
          setResponseCount(1);
        } else {
          // Vehicle not found — show generic help message
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: `${welcomeMsg} ${genericMsg}` },
          ]);
          // Count this as a response
          setResponseCount(1);
        }
      })
      .catch(() => {
        if (mounted) {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: `${welcomeMsg} ${genericMsg}` },
          ]);
          // Count this as a response
          setResponseCount(1);
        }
      });

    // Show the contact us form right away
    setOpenForm({ formId: "contactUs" });
    return () => {
      mounted = false;
    };
    // empty deps => run only once on mount
  }, []);

  const [messages, setMessages] = useState([
    {
      role: "system",
      content:
        "You are MotoMate (UK), a friendly car dealership assistant. Be concise, helpful, and transparent. Use UK English and GBP.",
    },
  ]);

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [openForm, setOpenForm] = useState(null); // e.g. { formId: "testDrive" }
  const [isPostingForm, setIsPostingForm] = useState(false);

  const [profile, setProfile] = useState({
    fullName: "",
    telephone: "",
    email: "",
  });

  const listRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    // auto-scroll to latest message
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    function onMsg(e) {
      if (e.data?.type === "MOTOMATE_WIDGET_CONFIG") {
        // e.data.payload = { vehicleReg, reserved, description, theme, ... }
        // store it in state/context and show your greeting
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Effect to detect when vehicleReg changes from UNKNOWN to a real registration
  useEffect(() => {
    const currentVehicleReg = vehicleReg?.toString().trim().toUpperCase();
    const previousVehicleReg = prevVehicleReg.current?.toString().trim().toUpperCase();
    
    // Only show message if there's an actual transition from UNKNOWN to a real vehicle reg
    // Don't show if the app opened directly with a real vehicle reg
    if (
      currentVehicleReg &&
      currentVehicleReg !== "UNKNOWN" &&
      previousVehicleReg === "UNKNOWN" &&
      !hasShownVehicleRegMessage.current &&
      messages.length > 1
    ) {
      hasShownVehicleRegMessage.current = true;
      
      const additionalButtonsMessage = "There are now additional buttons at the bottom of this window where you can either;\n- book a test drive/viewing\n- place a refundable reservation on the vehicle\n- watch a video demonstration\n- enquire about finance";
      
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: additionalButtonsMessage,
        },
      ]);
    }
    
    // Update the previous value for next comparison
    prevVehicleReg.current = vehicleReg;
  }, [vehicleReg, messages.length]);

  async function handleFormSubmitInlinePartEx(payload) {
    // Build the message exactly how you want it to appear in chat

    let text = "";
    if (payload.formId === "sellMyCar") {
      text += `Sell My Car details:\n`;
    } else {
      text += `My part exchange details are for vehicle: ${payload.vehicleReg}\n`;
    }

    text +=
      `Registration: ${payload.partExReg}\n` +
      `Mileage: ${payload.mileage}\n` +
      `Number of keys: ${payload.numKeys}\n` +
      `Service history: ${payload.serviceHistory}\n` +
      `Condition: ${payload.condition}\n` +
      `Notable damage or repairs: ${payload.damage}\n` +
      `Outstanding finance: ${payload.outstandingFinance}\n` +
      `Settlement figure: ${payload.settlement ?? "N/A"}\n` +
      `Valuation in mind: ${payload.valuation}\n` +
      `Essential to deal: ${payload.essential}\n\n` +
      `My contact details are:\n` +
      `Full name: ${payload.fullName}\n` +
      `Telephone: ${payload.telephone}\n` +
      `Email: ${payload.email}`;

    // Close the form
    setOpenForm(null);

    // Send as a normal user message so the assistant can reply as usual
    await sendMessage(text, {
      source: "form",
      payload: { type: "partExLead", ...payload }, // optional, handy for your backend/analytics
    });
  }

  async function handleFormSubmitInlineContactUs(payload) {
    // Build the message exactly how you want it to appear in chat
    let text = "";
    if (
      !payload.vehicleReg ||
      String(payload.vehicleReg).trim().toUpperCase() === "UNKNOWN"
    ) {
      text = `I would like to be contacted.\n`;
    } else {
      text = `I would like to be contacted about ${payload.vehicleReg}.\n`;
    }
    text +=
      `My contact details are:\n` +
      `Full name: ${payload.fullName}\n` +
      `Telephone: ${payload.telephone}\n` +
      `Email: ${payload.email} Thank you.\n`;

    // Close the form
    setOpenForm(null);

    // Send as a normal user message so the assistant can reply as usual
    await sendMessage(text, {
      source: "form",
      payload: { type: "contactUsLead", ...payload }, // optional, handy for your backend/analytics
    });
  }

  async function handleFormSubmitInline(payload) {
    // Build the message exactly how you want it to appear in chat
    const ukDate = formatDateToUK(payload.date);

    let title = "I would like a test drive for";
    if (payload.formId === "videoWalkaround") {
      title = "I would like a Video Walkaround appointment for";
    } else if (payload.formId === "oneToOneSalesCall") {
      title = "I would like to book a One to One sales call / appointment for";
    } else if (payload.formId === "carViewingAppointment") {
      title = "I would like to book a Car Viewing Appointment for";
    }

    const text =
      `${title} ${payload.vehicleReg}.\n` +
      `My contact details are:\n` +
      `Full name: ${payload.fullName}\n` +
      `Telephone: ${payload.telephone}\n` +
      `Email: ${payload.email}\n` +
      `Preferred date: ${ukDate}\n` +
      `Preferred time: ${payload.time}`;

    // Close the form
    setOpenForm(null);

    // Send as a normal user message so the assistant can reply as usual
    await sendMessage(text, {
      source: "form",
      payload: { type: "testDriveLead", ...payload }, // optional, handy for your backend/analytics
    });
  }

  async function handleFormSubmitFromCard(payload) {
    await handleFormSubmitInline(payload);
  }

  async function sendMessage(maybeText, opts = {}) {
    const raw = typeof maybeText === "string" ? maybeText : input;
    const trimmed = (raw || "").trim();
    if (!trimmed) return;

    setOpenForm(null);

    // Use override reg if provided (from the button), otherwise current state
    const effectiveReg = (
      opts.overrideVehicleReg ||
      vehicleReg ||
      ""
    ).toString();
    const normalizedVRM = effectiveReg.toUpperCase().replace(/\s+/g, "");

    // Build a fresh vehicleContext using the effective reg
    const vehicleContextForPost =
      "Vehicle context (do not reveal directly unless asked):\n" +
      `vehicleReg: ${effectiveReg}\n` +
      `reserved: ${reserved}\n` +
      `description: ${description}`;

    const newMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(newMessages);
    if (typeof maybeText !== "string") setInput("");
    setIsLoading(true);

    try {
      const clientSessionKey = "motomate_client_session_id";
      let clientId;
      try {
        clientId = window.localStorage.getItem(clientSessionKey);
        if (!clientId) {
          clientId =
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : "sess-" + Math.random().toString(36).slice(2, 10);
          window.localStorage.setItem(clientSessionKey, clientId);
        }
      } catch (e) {
        // localStorage unavailable (e.g. privacy mode) — fall back to in-memory id
        clientId = "sess-" + Math.random().toString(36).slice(2, 10);
      }

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: newMessages,
          vehicleContext: vehicleContextForPost, // <-- uses effective reg
          vehicleReg: normalizedVRM, // <-- uses effective reg
          reserved: reserved,
          clientId,
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `HTTP ${res.status}`);
      }

      let reply;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        reply = await res.json();
      } else {
        const text = await res.text();
        try {
          reply = JSON.parse(text);
        } catch {
          reply = text;
        }
      }

      setMessages((curr) => [
        ...curr,
        { role: "assistant", content: reply ?? "(no response)" },
      ]);

      // Increment response count and check if we should show the form
      setResponseCount((prev) => {
        const newCount = prev + 1;
        // Auto-open contactUs form after reaching the threshold
        if (
          newCount === RESPONSES_BEFORE_FORM_1ST ||
          newCount === RESPONSES_BEFORE_FORM_2ND
        ) {
          // Check if the response contains a forceForm CTA first
          const hasForceForm = checkForForceFormCTA(reply);
          if (!hasForceForm) {
            // Small delay to let the message render first
            setTimeout(() => {
              // Only auto-open contactUs form if profile is not yet set
              if (!profile.fullName || !profile.telephone || !profile.email) {
                setOpenForm({ formId: "contactUs" });
              }
            }, 500);
          }
        }
        return newCount;
      });
    } catch (err) {
      console.error(err);
      setMessages((curr) => [
        ...curr,
        {
          role: "assistant",
          content:
            "Sorry—I'm having trouble reaching the server. Please try again.",
        },
      ]);

      // Even on error, increment response count
      setResponseCount((prev) => prev + 1);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper function to check if a response contains forceForm CTAs
  function checkForForceFormCTA(content) {
    const card = normalizeToCard(content);
    if (card && card.type === "text" && Array.isArray(card.ctas)) {
      return card.ctas.some((cta) => cta.type === "forceForm");
    }
    return false;
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ fontWeight: 700 }}>MotoMate</div>
          {vehicleReg &&
            String(vehicleReg).trim().toUpperCase() !== "UNKNOWN" && (
              <div style={styles.subtle}>Chat about {vehicleReg}</div>
            )}
        </div>
        {/* Scrollable messages area */}
        <div ref={listRef} style={styles.chatScroller}>
          {messages
            .filter((m) => m.role !== "system")
            .map((m, i) => (
              <div
                key={i}
                style={{
                  ...styles.bubble,
                  ...(m.role === "user"
                    ? styles.bubbleUser
                    : styles.bubbleAssistant),
                }}
              >
                {renderMessage(m, {
                  onSend: sendMessage,
                  setVehicleReg,
                  setReserved,
                  setDescription,
                  vehicleReg,
                  setOpenForm,
                  handleFormSubmitFromCard,
                })}{" "}
                {/* << important */}
              </div>
            ))}
          {isLoading && <Thinking>Thinking…!!!</Thinking>}
          <div ref={bottomRef} />
        </div>
        {/* Inline form area (opens when a CTA asks to show a form) */}
        {(openForm?.formId === "partEx" ||
          openForm?.formId === "sellMyCar") && (
          <div style={{ padding: 8 }}>
            <PartExForm
              vehicleReg={vehicleReg}
              profile={profile}
              setProfile={setProfile}
              onCancel={() => setOpenForm(null)}
              onSubmitted={handleFormSubmitInlinePartEx}
              isPosting={isPostingForm}
              formId={openForm?.formId}
            />
          </div>
        )}
        {/* Inline form area (opens when a CTA asks to show a form) */}
        {openForm?.formId === "contactUs" && (
          <div style={{ padding: 8 }}>
            <ContactUsForm
              vehicleReg={vehicleReg}
              profile={profile}
              setProfile={setProfile}
              onCancel={() => setOpenForm(null)}
              onSubmitted={handleFormSubmitInlineContactUs}
              isPosting={isPostingForm}
            />
          </div>
        )}
        {/* Inline form area (opens when a CTA asks to show a form) */}
        {(openForm?.formId === "testDrive" ||
          openForm?.formId === "videoWalkaround" ||
          openForm?.formId === "oneToOneSalesCall" ||
          openForm?.formId === "carViewingAppointment") && (
          <div style={{ padding: 8 }}>
            <TestDriveForm
              vehicleReg={vehicleReg}
              profile={profile}
              setProfile={setProfile}
              onCancel={() => setOpenForm(null)}
              onSubmitted={handleFormSubmitInline}
              isPosting={isPostingForm}
              formId={openForm?.formId}
            />
          </div>
        )}
        {/* Sticky composer */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage();
          }}
          style={styles.composer}
        >
          <input
            style={styles.textInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              vehicleReg &&
              String(vehicleReg).trim().toUpperCase() !== "UNKNOWN"
                ? `Ask about ${vehicleReg}…`
                : "Ask me anything"
            }
            aria-label="Message MotoMate"
          />
          <button type="submit" disabled={isLoading} style={styles.primaryBtn}>
            Send
          </button>
        </form>
        {/* Small print */}
        <AvailabilityIndicator vehicleReg={vehicleReg} reserved={reserved} />
        {vehicleReg && String(vehicleReg).trim().toUpperCase() !== "UNKNOWN" ? (
          <FooterIconsWithRegistration sendMessage={sendMessage} />
        ) : (
          <FooterIconsNoRegistration sendMessage={sendMessage} />
        )}
      </div>
    </div>
  );
}

// ------------------ Styles ------------------
const styles = {
  wrapper: {
    height: "100%",
    background: "transparent",
    fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif', // use Arial as default
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    width: "100%",
    maxWidth: 420,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    padding: "10px 12px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "baseline",
    gap: 8,
  },
  subtle: { fontSize: 12, color: "#64748b" },
  chatScroller: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    scrollbarGutter: "stable",
  },
  bubble: {
    maxWidth: "85%",
    padding: "8px 10px",
    borderRadius: 12,
    boxShadow: "0 1px 1px rgba(0,0,0,0.04)",
    fontSize: 14,
    lineHeight: 1.4,
  },
  bubbleUser: { background: "#0f172a", color: "#fff", marginLeft: "auto" },
  bubbleAssistant: {
    background: "#fff",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
  },
  composer: {
    display: "flex",
    gap: 8,
    padding: 8,
    borderTop: "1px solid #e2e8f0",
    background: "#fff",
    zIndex: 1,
  },
  textInput: {
    flex: 1,
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 14,
    outline: "none",
  },
  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  },
  thinking: { fontSize: 12, color: "#64748b", padding: "0 8px" },
  footerNote: {
    fontSize: 11,
    color: "#94a3b8",
    padding: "6px 10px 10px",
    display: "flex",
    justifyContent: "center",
  },
  footerIcons: {
    fontSize: 12,
    color: "#94a3b8",
    padding: "6px 10px 10px",
    display: "flex",
    gap: "20px", // space between items
    justifyContent: "center",
  },

  iconItem: {
    display: "flex",
    flexDirection: "column", // stack icon above text
    alignItems: "center",
    cursor: "pointer",
    textAlign: "center",
  },

  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "#E4B343",
    color: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    marginBottom: 4, // space between circle and label
    transition: "background 0.2s",
  },
};

function AvailabilityIndicator({ vehicleReg, reserved }) {
  // if vehicleReg not set or UNKNOWN, do not show anything
  if (!vehicleReg || String(vehicleReg).trim().toUpperCase() === "UNKNOWN") {
    return null;
  }

  return (
    <div style={styles.footerNote}>
      {reserved
        ? "This vehicle is currently marked as reserved."
        : "This vehicle is currently available."}
    </div>
  );
}

function FooterIconsWithRegistration({ sendMessage }) {
  return (
    <div style={styles.footerIcons}>
      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="View video"
          onClick={() => sendMessage("Is there a video available")}
        >
          <FaVideo />
        </div>
        <span>Video</span>
      </div>

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Finance"
          onClick={() => sendMessage("What finance options do you offer")}
        >
          <FaPoundSign />
        </div>
        <span>Finance</span>
      </div>

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Part-ex"
          onClick={() => sendMessage("Can I part-ex my car")}
        >
          <FaExchangeAlt />
        </div>
        <span>Part-ex</span>
      </div>

      {/* <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Test drive"
          onClick={() => sendMessage("Can I book a test drive")}
        >
          <FaCar />
        </div>
        <span>Test Drive</span>
      </div> */}

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Book Appointment"
          onClick={() => sendMessage("Can I book an appointment")}
        >
          <FaCalendarAlt />
        </div>
        <span>Book</span>
      </div>

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Reserve Vehicle"
          onClick={() => sendMessage("Can I reserve this vehicle")}
        >
          <FaTicketAlt />
        </div>
        <span>Reserve</span>
      </div>
    </div>
  );
}

function FooterIconsNoRegistration({ sendMessage }) {
  return (
    <div style={styles.footerIcons}>
      {/* <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Our Stock"
          onClick={() => sendMessage("What vehicles do you have available")}
        >
          <FaCarSide />
        </div>
        <span>Our Stock</span>
      </div> */}

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Search Vehicles"
          onClick={() => sendMessage("Can you help me find a new vehicle")}
        >
          <FaSearch />
        </div>
        <span>Find a Car</span>
      </div>

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Finance"
          onClick={() => sendMessage("What finance options do you offer")}
        >
          <FaPoundSign />
        </div>
        <span>Finance</span>
      </div>

      <div style={styles.iconItem}>
        <div
          style={styles.iconCircle}
          title="Sell my Car"
          onClick={() => sendMessage("Can you buy my car")}
        >
          <MdSell />
        </div>
        <span>Sell my Car</span>
      </div>
    </div>
  );
}
